/* iter-53l — Save As dialog.
 *
 * Replaces the previous no-op `toast.success('Saved as new project')`
 * with a real two-option choice:
 *   1. Save to cloud (database) — duplicates the current project as a
 *      new row with a user-supplied title.
 *   2. Save to device — downloads the manuscript as a .docx (default)
 *      or .txt to the user's local filesystem. Re-uses the existing
 *      `exportExtrasApi` plumbing.
 *
 * Lives in /chrome because it's wired from the MenuBar.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button }     from '@/components/ui/button';
import { Input }      from '@/components/ui/input';
import { Label }      from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, Cloud, HardDrive, Save, FileCode2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { exportExtrasApi } from '@/lib/api';
import { usePW } from '@/lib/store';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function SaveAsDialog({ open, onOpenChange }) {
  const { project, refreshProjects } = usePW();
  const [destination, setDestination] = useState('cloud');
  const [title, setTitle]             = useState('');
  const [format, setFormat]           = useState('docx');
  const [busy, setBusy]               = useState(false);
  // iter-53n++ — Save-as-template toggle. Only meaningful when
  // destination === 'cloud'. When on, the backend strips prose from the
  // cloned chapters so the new project is an outline-only stencil.
  const [asTemplate, setAsTemplate]   = useState(false);

  React.useEffect(() => {
    if (open && project) {
      // iter-53n++ — Project rows expose `name` (not `title`); the old
      // `project.title` fallback rendered "Untitled (copy)" for everyone.
      setTitle(`${project.name || project.title || 'Untitled'} (copy)`);
      setAsTemplate(false);
    }
  }, [open, project]);

  const save = async () => {
    if (!project) {
      toast.error('No project loaded.');
      return;
    }
    setBusy(true);
    try {
      if (destination === 'cloud') {
        // iter-53n++ — Forward `as_template` so the backend can strip
        // chapter prose while keeping outline + characters + bible.
        await axios.post(`${API}/projects/${project.id}/clone`,
          { title, as_template: asTemplate });
        toast.success(asTemplate
          ? `Template saved: ${title}`
          : `Saved as new project: ${title}`);
        if (refreshProjects) await refreshProjects();
      } else {
        // Trigger a manuscript download to the user's device.
        const url = exportExtrasApi.manuscriptUrl
          ? exportExtrasApi.manuscriptUrl(project.id, format)
          : `${API}/projects/${project.id}/export/manuscript.${format}`;
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(title || project.name || project.title || 'manuscript')}.${format}`;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(`Downloading ${format.toUpperCase()}…`);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Save failed: ${e.message || e}`);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-as-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Save className="h-4 w-4" /> Save As</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RadioGroup value={destination} onValueChange={setDestination} className="space-y-2">
            <label htmlFor="dest-cloud" className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3 hover:bg-secondary/40">
              <RadioGroupItem id="dest-cloud" value="cloud" data-testid="save-as-cloud" className="mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold"><Cloud className="h-3.5 w-3.5" /> Save to cloud (database)</div>
                <div className="text-[11.5px] text-muted-foreground">Duplicates this project under a new name. Stays in your workspace.</div>
              </div>
            </label>
            <label htmlFor="dest-device" className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3 hover:bg-secondary/40">
              <RadioGroupItem id="dest-device" value="device" data-testid="save-as-device" className="mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold"><HardDrive className="h-3.5 w-3.5" /> Save to device</div>
                <div className="text-[11.5px] text-muted-foreground">Downloads the manuscript to your computer.</div>
              </div>
            </label>
          </RadioGroup>

          {destination === 'cloud' && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="save-as-title" className="text-[11.5px]">New project title</Label>
                <Input id="save-as-title" value={title} onChange={(e) => setTitle(e.target.value)}
                       data-testid="save-as-title" autoFocus />
              </div>
              {/* iter-53n++ — Save-as-template toggle. */}
              <label htmlFor="save-as-template" className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-secondary/20 p-2.5 hover:bg-secondary/40">
                <input
                  id="save-as-template"
                  type="checkbox"
                  checked={asTemplate}
                  onChange={(e) => setAsTemplate(e.target.checked)}
                  data-testid="save-as-template-toggle"
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
                    <FileCode2 className="h-3.5 w-3.5" /> Save as template
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Keep the outline, characters, variables and story bible — but wipe the
                    chapter prose so you can start a fresh novel from this stencil.
                  </div>
                </div>
              </label>
            </div>
          )}

          {destination === 'device' && (
            <div className="space-y-1.5">
              <Label className="text-[11.5px]">File format</Label>
              <div className="flex gap-2">
                {['docx', 'txt', 'pdf'].map((f) => (
                  <button key={f} type="button" onClick={() => setFormat(f)}
                          data-testid={`save-as-format-${f}`}
                          className={`rounded-md border px-3 py-1 text-[11.5px] uppercase tracking-wide ${format === f ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-secondary'}`}>
                    {f}
                  </button>
                ))}
              </div>
              <div className="pt-1 text-[11px] text-muted-foreground">Filename: <code className="font-mono-stat">{(title || 'manuscript')}.{format}</code></div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="save-as-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || (destination === 'cloud' && !title.trim())}
                  data-testid="save-as-submit">
            {busy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving…</> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
